import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { auth } from '@/boot/firebase'
import { api, getBaseApiUrl } from '@/boot/axios'
import type { User } from 'firebase/auth'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const user_profile = ref<any | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const authInitialized = ref(false)
  const requiresProjectSetup = ref(false)

  // Getters
  const isAuthenticated = computed(() => user.value !== null)
  const userEmail = computed(() => user.value?.email || '')
  const userName = computed(() => user.value?.displayName || '')
  const userId = computed(() => user.value?.uid || '')
  
  // User profile getters for the new structure
  const userData = computed(() => user_profile.value?.user || null)
  const subscriptionData = computed(() => user_profile.value?.subscription || null)
  const userPermission = computed(() => user_profile.value?.permission || '')
  const userTenant = computed(() => user_profile.value?.tenant || '')
  const userDomain = computed(() => user_profile.value?.domain || '')
  const defaultCalendar = computed(() => user_profile.value?.default_calendar || null)

  /** True when Firebase auth state has been resolved (user or null) */
  const isAuthReady = computed(() => authInitialized.value)

  /** True when fully authenticated with profile loaded */
  const isFullyAuthenticated = computed(() =>
    user.value !== null && user_profile.value !== null
  )

  // Actions
  const setUser = (newUser: User | null) => {
    user.value = newUser
  }

  const setMe = (data: any) => {
    // Update user profile with WebSocket data
    user_profile.value = data
    console.log('Updated user profile from WebSocket:', data)
  }

  const setLoading = (isLoading: boolean) => {
    loading.value = isLoading
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const clearError = () => {
    error.value = null
  }

  /**
   * Fetch user profile from /me endpoint.
   * Must be called AFTER enrollment selection sets the domain.
   * Returns the profile data, or null if failed.
   * Sets requiresProjectSetup=true if backend returns "Requires Tenant Creation".
   */
  const getUserProfile = async () => {
    try {
      const response = await api.get('/api/v1/me/')
      user_profile.value = response.data
      requiresProjectSetup.value = false
      
      // Extract data from the new structure
      const domain = response.data.domain || ''
      const subscription = response.data.subscription || null
      const currency = response.data.currency || 'USD'
      localStorage.setItem('currency', currency)
      localStorage.setItem('domain', domain)
      
      // Store subscription data for easy access
      if (subscription) {
        localStorage.setItem('subscription', JSON.stringify(subscription))
      }
      
      return response.data
    } catch (err: any) {
      // Check for "Requires Tenant Creation" (404)
      if (err.response?.status === 404) {
        const errorData = err.response?.data
        if (errorData?.error === 'Requires Tenant Creation') {
          console.log('[Auth] Requires Tenant Creation — redirecting to project setup')
          requiresProjectSetup.value = true
          user_profile.value = null
          return null
        }
      }
      console.error('Failed to fetch user profile:', err)
      setError('Failed to fetch user profile')
      return null
    }
  }

  const signInWithEmail = async (email: string, password: string) => {
    setLoading(true)
    clearError()

    try {
      const result = await auth.signInWithEmail(email, password)
      
      if (result.error) {
        throw new Error(result.error)
      }
      
      if (result.user) {
        setUser(result.user)
        
        // Get Firebase ID token and store it
        const token = await result.user.getIdToken()
        
        // Update localStorage for compatibility
        localStorage.setItem('isAuthenticated', 'true')
        localStorage.setItem('userId', result.user.uid)
        localStorage.setItem('userEmail', result.user.email || '')
        localStorage.setItem('userName', result.user.displayName || email)
        localStorage.setItem('authToken', token)
      }
      
      return { success: true, error: null }
    } catch (err: any) {
      setError(err.message)
      return { success: false, error: err.message }
    } finally {
      setLoading(false)
    }
  }

  const signUpWithEmail = async (email: string, password: string) => {
    setLoading(true)
    clearError()

    try {
      const result = await auth.signUpWithEmail(email, password)
      
      if (result.error) {
        throw new Error(result.error)
      }
      
      if (result.user) {
        setUser(result.user)
        
        // Get Firebase ID token and store it
        const token = await result.user.getIdToken()
        
        // Update localStorage for compatibility
        localStorage.setItem('isAuthenticated', 'true')
        localStorage.setItem('userId', result.user.uid)
        localStorage.setItem('userEmail', result.user.email || '')
        localStorage.setItem('userName', result.user.displayName || email)
        localStorage.setItem('authToken', token)
      }
      
      return { success: true, error: null }
    } catch (err: any) {
      setError(err.message)
      return { success: false, error: err.message }
    } finally {
      setLoading(false)
    }
  }

  const signInWithGoogle = async () => {
    setLoading(true)
    clearError()

    try {
      const result = await auth.signInWithGoogle()
      
      if (result.error) {
        throw new Error(result.error)
      }
      
      if (result.user) {
        setUser(result.user)
        
        // Get Firebase ID token and store it
        const token = await result.user.getIdToken()
        
        // Update localStorage for compatibility
        localStorage.setItem('isAuthenticated', 'true')
        localStorage.setItem('userId', result.user.uid)
        localStorage.setItem('userEmail', result.user.email || '')
        localStorage.setItem('userName', result.user.displayName || '')
        localStorage.setItem('authToken', token)
      }
      
      return { success: true, error: null }
    } catch (err: any) {
      setError(err.message)
      return { success: false, error: err.message }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    setLoading(true)
    clearError()

    try {
      await auth.signOut()
      setUser(null)
      user_profile.value = null
      requiresProjectSetup.value = false
      authInitialized.value = false

      // Clear all localStorage
      localStorage.clear()
      return { success: true, error: null }
    } catch (err: any) {
      setError(err.message)
      return { success: false, error: err.message }
    } finally {
      setLoading(false)
    }
  }

  /**
   * Initialize Firebase auth state listener.
   * Returns the unsubscribe function.
   */
  const initializeAuth = () => {
    return auth.onAuthStateChanged(async (firebaseUser) => {
      setUser(firebaseUser)
      
      if (firebaseUser) {
        // Get Firebase ID token and store it
        const token = await firebaseUser.getIdToken()
        
        // Update localStorage when user state changes
        localStorage.setItem('isAuthenticated', 'true')
        localStorage.setItem('userId', firebaseUser.uid)
        localStorage.setItem('userEmail', firebaseUser.email || '')
        localStorage.setItem('userName', firebaseUser.displayName || firebaseUser.email || '')
        localStorage.setItem('authToken', token)
      } else {
        // Only clear if we're actually initialized (not initial null on cold start)
        if (authInitialized.value) {
          localStorage.clear()
          user_profile.value = null
          requiresProjectSetup.value = false
        }
      }

      authInitialized.value = true
    })
  }

  // Standalone billing methods
  const isStandaloneMode = ref(false)

  const validateStandaloneToken = async (token: string) => {
    try {
      setLoading(true)
      const response = await api.post('/api/v1/auth/validate-token', { token })
      const data = response.data.user
      
      user_profile.value = data
      isStandaloneMode.value = true
      
      sessionStorage.setItem('standalone_billing_token', token)
      sessionStorage.setItem('standalone_billing_mode', 'true')
      
      return data
    } catch (err: any) {
      console.error('Standalone token validation failed:', err)
      throw new Error('Invalid or expired token')
    } finally {
      setLoading(false)
    }
  }

  const createStandaloneSession = (data: any) => {
    user_profile.value = data
    isStandaloneMode.value = true
    sessionStorage.setItem('standalone_billing_mode', 'true')
  }

  const isInStandaloneMode = computed(() => {
    return isStandaloneMode.value || sessionStorage.getItem('standalone_billing_mode') === 'true'
  })

  const getMobileReturnUrl = () => {
    return sessionStorage.getItem('billing_return_url') || null
  }

  const clearStandaloneSession = () => {
    isStandaloneMode.value = false
    sessionStorage.removeItem('standalone_billing_token')
    sessionStorage.removeItem('standalone_billing_mode')
    sessionStorage.removeItem('billing_return_url')
    user_profile.value = null
  }

  return {
    // State
    user,
    user_profile,
    loading,
    error,
    authInitialized,
    requiresProjectSetup,
    isStandaloneMode,
    
    // Getters
    isAuthenticated,
    isAuthReady,
    isFullyAuthenticated,
    userEmail,
    userName,
    userId,
    userData,
    subscriptionData,
    userPermission,
    userTenant,
    userDomain,
    defaultCalendar,
    isInStandaloneMode,
    
    // Actions
    setUser,
    setMe,
    setLoading,
    setError,
    clearError,
    getUserProfile,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    initializeAuth,
    // Standalone billing actions
    validateStandaloneToken,
    createStandaloneSession,
    getMobileReturnUrl,
    clearStandaloneSession
  }
})
